import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlainDate } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

export interface DevConnectPresenter {
  id: string;
  name: string;
  departmentName: string | null;
}

export interface DevConnectBooking {
  id: string;
  slotIndex: number;
  presenter: DevConnectPresenter;
  title: string;
  notes: string | null;
  demoUrl: string | null;
  slidesUrl: string | null;
  prUrl: string | null;
  bookedAt: string;
  /** Whether the viewer may give this slot back. Resolved by the API. */
  canRelease: boolean;
}

/** Why the sheet will not take a booking from this viewer. */
export type BookBlockedReason = 'CLOSED' | 'FULL' | 'ALREADY_BOOKED' | 'DEPARTMENT' | 'HELD';

export interface DevConnectSession {
  /** Null for a Friday inside the horizon that nobody has materialised yet. */
  id: string | null;
  heldOn: PlainDate;
  startsAtLocal: string;
  capacity: number;
  theme: string | null;
  heldAt: string | null;
  heldNotes: string | null;
  heldByName: string | null;
  allowedDepartments: Array<{ id: string; name: string }>;
  bookings: DevConnectBooking[];
  openSlots: number[];
  /**
   * Resolved server-side. The page never re-derives the rule - a sheet that
   * disagrees with the API about who may sign it offers a button that 400s.
   */
  canBook: boolean;
  bookBlockedReason: BookBlockedReason | null;
  closesAt: string;
  /** The viewer is through only because they are an admin. */
  bookingIsOverride: boolean;
}

export interface ShowcaseEntry extends DevConnectBooking {
  heldOn: PlainDate;
  sessionNotes: string | null;
}

export function useDevConnectSessions() {
  return useQuery<DevConnectSession[]>({
    queryKey: ['dev-connect', 'sessions'],
    queryFn: () => api.get<DevConnectSession[]>('/dev-connect/sessions'),
  });
}

/** The showcase log. Slow-moving, so it is left stale for longer. */
export function useShowcaseLog(
  filter: { employeeId?: string; from?: string; to?: string; quarterId?: string } = {},
) {
  const params = new URLSearchParams();
  if (filter.employeeId) params.set('employeeId', filter.employeeId);
  if (filter.quarterId) params.set('quarterId', filter.quarterId);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  const qs = params.toString();

  return useQuery<ShowcaseEntry[]>({
    queryKey: ['dev-connect', 'log', filter],
    queryFn: () => api.get<ShowcaseEntry[]>(`/dev-connect/log${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60_000,
  });
}

export interface BookInput {
  heldOn: PlainDate;
  slotIndex: number;
  title: string;
  notes?: string | null;
  demoUrl?: string | null;
  slidesUrl?: string | null;
  prUrl?: string | null;
  presenterId?: string;
}

/**
 * Claim a slot.
 *
 * A 409 means somebody else got there first, so the sheet in front of this person
 * is already out of date - refetching is the useful half of the response, and the
 * message tells them which of the two collisions it was.
 */
export function useBookSlot() {
  const queryClient = useQueryClient();
  return useMutation<DevConnectBooking, ApiError, BookInput>({
    mutationFn: (body) => api.post<DevConnectBooking>('/dev-connect/bookings', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
    onError: (error) => {
      if (error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['dev-connect'] });
      }
    },
  });
}

export function useReleaseSlot() {
  const queryClient = useQueryClient();
  return useMutation<{ released: boolean }, ApiError, { id: string; reason?: string | null }>({
    mutationFn: ({ id, reason }) =>
      api.post<{ released: boolean }>(`/dev-connect/bookings/${id}/release`, { reason }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
  });
}

export function useUpdateBooking() {
  const queryClient = useQueryClient();
  return useMutation<
    DevConnectBooking,
    ApiError,
    { id: string; title?: string; notes?: string | null; demoUrl?: string | null; slidesUrl?: string | null; prUrl?: string | null }
  >({
    mutationFn: ({ id, ...body }) =>
      api.patch<DevConnectBooking>(`/dev-connect/bookings/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
  });
}

export function useMarkSessionHeld() {
  const queryClient = useQueryClient();
  return useMutation<DevConnectSession, ApiError, { id: string; notes: string }>({
    mutationFn: ({ id, notes }) =>
      api.post<DevConnectSession>(`/dev-connect/sessions/${id}/held`, { notes }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
  });
}

/**
 * Open a session. SUPER_ADMIN only, and the only way one comes into existence.
 *
 * There used to be no hook for this even though the route existed, because the
 * worker opened every Friday and an admin only ever edited one. The generator is
 * gone - sessions are fortnightly and chosen by a person - so this is now the
 * whole supply.
 *
 * `departmentIds` is deliberately omitted rather than sent empty: the API reads
 * that as "use the configured default", and an empty array as "nobody", which
 * would publish a sheet no one could book.
 */
export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation<
    DevConnectSession,
    ApiError,
    {
      heldOn: PlainDate;
      startsAtLocal?: string;
      capacity?: number;
      theme?: string | null;
    }
  >({
    mutationFn: (body) => api.post<DevConnectSession>('/dev-connect/sessions', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation<
    DevConnectSession,
    ApiError,
    {
      id: string;
      /** Moving the date takes everyone booked along; the API notifies them. */
      heldOn?: PlainDate;
      capacity?: number;
      startsAtLocal?: string;
      theme?: string | null;
      departmentIds?: string[];
    }
  >({
    mutationFn: ({ id, ...body }) => api.patch<DevConnectSession>(`/dev-connect/sessions/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dev-connect'] }),
  });
}
